"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { motion, AnimatePresence, useInView } from "framer-motion"
import Image from "next/image"
import {
  Link2, MapPin, TrendingUp, Cloud, Settings2, Users,
  FileSearch, Shield, GitBranch, BarChart3, Smartphone,
  Building2, CheckCircle2, LucideIcon
} from "lucide-react"
import type { Solution, SolutionReason } from "@/types/solution"

const ICON_MAP: Record<string, LucideIcon> = {
  Link2, MapPin, TrendingUp, Cloud, Settings2, Users,
  FileSearch, Shield, GitBranch, BarChart3, Smartphone,
  Building2, CheckCircle2,
}

export interface WhyChooseSectionProps {
  solution: Pick<Solution, "whyTagline" | "whySubtitle" | "reasons">
}

const BLUE = "#2C78B9"
const BLUE_004 = "rgba(44,120,185,0.04)"
const BLUE_006 = "rgba(44,120,185,0.06)"
const BLUE_008 = "rgba(44,120,185,0.08)"
const BLUE_010 = "rgba(44,120,185,0.10)"
const BLUE_012 = "rgba(44,120,185,0.12)"
const BLUE_015 = "rgba(44,120,185,0.15)"
const BLUE_025 = "rgba(44,120,185,0.25)"
const BLUE_030 = "rgba(44,120,185,0.30)"

export function WhyChooseSection({ solution }: WhyChooseSectionProps) {
  const reasons = solution.reasons ?? []
  const total = reasons.length

  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const [swipeDir, setSwipeDir] = useState<1 | -1>(1)
  const interactionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const inView = useInView(sectionRef, { once: true, margin: "-80px" })

  /* ── Auto-advance ──────────────────────────────────────────────────────── */
  useEffect(() => {
    if (paused || total === 0) return
    const id = setInterval(() => {
      setSwipeDir(1)
      setActive(prev => (prev + 1) % total)
    }, 4000)
    return () => clearInterval(id)
  }, [paused, total])

  const handleInteraction = useCallback((idx: number) => {
    setSwipeDir(idx > active ? 1 : -1)
    setActive(idx)
    setPaused(true)
    if (interactionTimer.current) clearTimeout(interactionTimer.current)
    interactionTimer.current = setTimeout(() => setPaused(false), 3000)
  }, [active])

  const handleMouseEnter = useCallback(() => {
    setPaused(true)
    if (interactionTimer.current) clearTimeout(interactionTimer.current)
  }, [])

  const handleMouseLeave = useCallback(() => {
    interactionTimer.current = setTimeout(() => setPaused(false), 3000)
  }, [])

  /* ── Swipe handlers for detail panel ──────────────────────────────────── */
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return
    const dx = e.changedTouches[0].clientX - touchStartX.current
    const dy = e.changedTouches[0].clientY - touchStartY.current
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return
    const nextIdx = dx < 0
      ? (active + 1) % total
      : (active - 1 + total) % total
    handleInteraction(nextIdx)
    touchStartX.current = null
    touchStartY.current = null
  }

  useEffect(() => () => {
    if (interactionTimer.current) clearTimeout(interactionTimer.current)
  }, [])

  if (total === 0) return null

  const current = reasons[active]
  const IconComp = ICON_MAP[current.icon] ?? Link2

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden border-t border-neutral-100"
      style={{ background: "#ffffff" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Background ─────────────────────────────────────────────────── */}
      <div className="absolute inset-0 z-0 pointer-events-none select-none overflow-hidden">
        <Image src="/images/bg-img/bg-3.webp" alt="" fill className="object-cover opacity-[0.25]" priority={false} />
        <div className="absolute top-1/4 right-0 w-[700px] h-[700px] bg-blue-100/20 rounded-full blur-[140px] translate-x-1/3" />
        <div className="absolute bottom-1/4 left-0 w-[600px] h-[600px] bg-indigo-100/20 rounded-full blur-[140px] -translate-x-1/4" />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-white/40" />
      </div>

      {/*
        PADDING FIX:
        Mobile:  py-12 px-4  (was hardcoded 64px / 32px — too generous on phones)
        Desktop: py-16 px-8 up to max-w via mx-auto
      */}
      <div
        className="relative z-10 mx-auto w-full"
        style={{ maxWidth: 1200, padding: "clamp(48px,6vw,80px) clamp(16px,4vw,48px)" }}
      >

        {/* ── Section Header ─────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="text-center flex flex-col items-center"
          style={{ marginBottom: "clamp(32px,4vw,48px)" }}
        >
          {/* Badge */}
          <div style={{ marginBottom: 16 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "5px 14px", borderRadius: 999,
              background: BLUE_008, border: `1px solid ${BLUE_025}`,
            }}>
              <span style={{ position: "relative", display: "inline-flex", width: 8, height: 8 }}>
                <span style={{
                  position: "absolute", inset: 0, borderRadius: "50%",
                  background: BLUE, opacity: 0.4,
                  animation: "why-ping 1.6s cubic-bezier(0,0,0.2,1) infinite",
                }} />
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: BLUE, display: "block" }} />
              </span>
              <span style={{ fontSize: 11, fontWeight: 800, color: BLUE, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                WHY Mx-ERP
              </span>
            </div>
          </div>

          {/* Headline
              Mobile: clamp starts at 1.5rem so two stacked h2s don't eat too much vertical space
              Desktop: up to 2.5rem unchanged
          */}
          <div style={{ marginBottom: 14 }}>
            <h2 style={{
              fontWeight: 900, color: "#0f172a",
              fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
              letterSpacing: "-0.03em", lineHeight: 1.15, margin: "0 0 2px 0",
            }}>
              One Platform.
            </h2>
            <h2 style={{
              fontWeight: 900, color: BLUE,
              fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
              letterSpacing: "-0.03em", lineHeight: 1.15, margin: 0,
            }}>
              Every Department. No Data Silos.
            </h2>
          </div>

          <p style={{
            fontSize: "clamp(0.9rem, 1.2vw, 1.1rem)", color: "#475569",
            lineHeight: 1.65, maxWidth: 680, margin: 0, fontWeight: 500,
          }}>
            {solution.whySubtitle}
          </p>
        </motion.div>

        {/* ══════════════════════════════════════════════════════════════════
            TWO-COLUMN LAYOUT
            Desktop: 38/62 grid  ← more room for content panel
            Mobile:  single column, tabs on top as horizontal scroll row
        ══════════════════════════════════════════════════════════════════ */}
        <div className="why-grid">

          {/* ── LEFT — Tab List ──────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
            className="why-tabs-col"
          >
            {/* ── DESKTOP: vertical tab list ─────────────────────────────── */}
            <div className="why-tabs-desktop">
              {reasons.map((r, i) => {
                const Ico = ICON_MAP[r.icon] ?? Link2
                const isActive = i === active
                return (
                  <motion.button
                    key={r.num}
                    onClick={() => handleInteraction(i)}
                    initial={{ opacity: 0, x: -40 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.45, ease: "easeOut", delay: 0.12 + i * 0.06 }}
                    style={{
                      all: "unset", display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 14px", borderRadius: 12,
                      cursor: "pointer", transition: "all 0.25s ease",
                      border: isActive ? `1px solid ${BLUE_025}` : "1px solid transparent",
                      background: isActive ? BLUE_008 : "transparent",
                      boxShadow: isActive ? `0 2px 12px ${BLUE_008}` : "none",
                      borderLeft: isActive ? `3px solid ${BLUE}` : "3px solid transparent",
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        (e.currentTarget as HTMLElement).style.background = BLUE_004
                          ; (e.currentTarget as HTMLElement).style.borderColor = BLUE_010
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        (e.currentTarget as HTMLElement).style.background = "transparent"
                          ; (e.currentTarget as HTMLElement).style.borderColor = "transparent"
                      }
                    }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: isActive ? BLUE_012 : BLUE_006,
                      border: `0.5px solid ${isActive ? BLUE_030 : BLUE_015}`,
                      transition: "all 0.25s ease",
                    }}>
                      <Ico size={18} color={BLUE} strokeWidth={1.5} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 14, fontWeight: 600,
                        color: isActive ? "#0f172a" : "#64748b",
                        transition: "color 0.2s",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {r.title}
                      </div>
                    </div>
                  </motion.button>
                )
              })}
            </div>
          </motion.div>

          {/* ── RIGHT — Detail Panel ─────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.2 }}
            style={{
              borderRadius: 20, border: "1px solid #e2e8f0",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              position: "relative",
              overflow: "hidden",
              background: "#ffffff",
              minHeight: 420,
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Watermark icon — clipped by overflow:hidden, won't cause mobile scroll */}
            <div style={{ position: "absolute", bottom: -20, right: -20, pointerEvents: "none", zIndex: 0 }}>
              <IconComp size={130} color="rgba(44,120,185,0.04)" strokeWidth={1} />
            </div>

            {/* Animated content
                FIX: mode="popLayout" so exit+enter overlap — no empty frame.
                Content area has padding scaled by clamp so it's never too tight.
            */}
            <AnimatePresence mode="popLayout">
              <motion.div
                key={active}
                custom={swipeDir}
                variants={{
                  enter: (d: number) => ({ opacity: 0, x: d * 24 }),
                  center: { opacity: 1, x: 0 },
                  exit: (d: number) => ({ opacity: 0, x: d * -24 }),
                }}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  position: "relative", zIndex: 1,
                  /*
                    Padding:
                    Mobile:  20px 20px  — was 40px horizontal, too tight on 375px
                    Desktop: 36px 40px  — original
                  */
                  padding: "clamp(20px,3vw,36px) clamp(20px,4vw,40px)",
                }}
              >
                {/* Icon */}
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: BLUE_010, border: `1px solid ${BLUE_025}`,
                }}>
                  <IconComp size={24} color={BLUE} strokeWidth={1.5} />
                </div>

                {/* Title
                    Mobile: 18px, Desktop: 22px
                */}
                <h3 style={{
                  fontSize: "clamp(17px, 2vw, 22px)",
                  fontWeight: 800, color: "#0f172a",
                  marginTop: 14, marginBottom: 0, lineHeight: 1.25,
                }}>
                  {current.title}
                </h3>

                {/* Description */}
                <p style={{
                  fontSize: "clamp(13px, 1.1vw, 14px)",
                  color: "#475569", lineHeight: 1.75,
                  marginTop: 10, marginBottom: 0,
                  maxWidth: 480,
                  overflowWrap: "break-word",
                }}>
                  {current.description}
                </p>

                {/* Divider */}
                <div style={{ height: 1, background: "#f1f5f9", margin: "18px 0" }} />

                {/* Impact metric
                    Mobile: stacked (flex-col) so value + label never overflow side-by-side
                    Desktop: row (flex-row) — original
                */}
                <div style={{
                  background: BLUE_004, borderRadius: 12,
                  border: `1px solid ${BLUE_012}`,
                  padding: "clamp(12px,1.5vw,14px) clamp(14px,2vw,18px)",
                }}>
                  <div className="why-metric">
                    <span style={{
                      fontSize: "clamp(26px, 3vw, 32px)",
                      fontWeight: 900, color: BLUE, lineHeight: 1, flexShrink: 0,
                      overflowWrap: "break-word",
                    }}>
                      {current.metric.value}
                    </span>
                    <span style={{
                      fontSize: "clamp(12px, 1vw, 13px)",
                      color: "#475569", lineHeight: 1.5,
                    }}>
                      {current.metric.label.split("\\n").map((line, i) => (
                        <span key={i} style={{ display: "block" }}>{line}</span>
                      ))}
                    </span>
                  </div>
                </div>

                {/* Feature tags
                    Mobile: smaller padding so tags don't wrap to a third line
                */}
                <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {current.tags.map(tag => (
                    <span
                      key={tag}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        background: BLUE_006, border: `1px solid ${BLUE_015}`,
                        borderRadius: 8,
                        padding: "5px 10px",
                        fontSize: 11, fontWeight: 600, color: BLUE,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <CheckCircle2 size={11} color="#059A8F" strokeWidth={2.5} />
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* ── Progress dots ─────────────────────────────────────────────── */}
        <div className="why-dots">
          {reasons.map((_, i) => (
            <button
              key={i}
              onClick={() => handleInteraction(i)}
              style={{
                all: "unset",
                width: i === active ? 20 : 6, height: 6, borderRadius: 3,
                background: i === active ? BLUE : "rgba(44,120,185,0.2)",
                cursor: "pointer", transition: "all 0.3s ease",
              }}
            />
          ))}
        </div>

      </div>

      {/* ── Scoped styles ──────────────────────────────────────────────────── */}
      <style>{`
        @keyframes why-ping {
           75%, 100% { transform: scale(2); opacity: 0; }
         }

         /* ── Desktop grid — 38/62 split (was 44/56) ── */
         .why-grid {
           display: grid;
           grid-template-columns: 38% 62%;
           gap: 24px;
           align-items: start;
         }
         .why-tabs-col {
           position: sticky;
           top: 80px;
         }
         .why-tabs-desktop { display: flex; flex-direction: column; gap: 2px; }

         /* Mobile wrapper for horizontal tabs with fade affordances */
         .why-tabs-mobile-wrap { display: none; }
         .why-dots { display: none; }

         /* Metric: row on desktop */
         .why-metric {
           display: flex;
           align-items: center;
           gap: 14px;
         }

         /* ── Tablet 768–1024px ── */
         @media (max-width: 1024px) {
           .why-grid {
             grid-template-columns: 42% 58%;
             gap: 20px;
           }
           .why-tabs-col { position: static; }
         }

         /* ── Mobile < 768px ── */
         @media (max-width: 767px) {
           .why-grid {
             grid-template-columns: 1fr;
             gap: 12px;
           }
           .why-tabs-col { position: static; }
            .why-tabs-desktop { display: none; }

            .why-tabs-col { display: none; }

            /* Dots below panel on mobile */
            .why-dots {
              display: flex;
              justify-content: center;
              gap: 6px;
              margin-top: 16px;
            }

           /* Metric: column on mobile so value + label never overflow */
           .why-metric {
             flex-direction: column;
             align-items: flex-start;
             gap: 6px;
           }
         }
       `}</style>
    </section>
  )
}